import PropTypes from "prop-types";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useFormContext, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Spinner } from "@/components/ui/spinner";
import {
  BaseForm,
  FormInput,
  FormSelect,
  FormToggle,
} from "components/BaseForm";
import { CALL_DISPOSITION_TYPES } from "constants/Constants";
import { useCallDispositionsMutations } from "hooks/useCallDispositionsTable";
import CallDispositionService from "services/CallDispositions";

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  type: z.string().min(1, "Type is required"),
  parent_id: z.number().nullable(),
  dnc: z.boolean(),
  invalid: z.boolean(),
  is_successful: z.boolean(),
});

const dispositionTypeOptions = Object.values(CALL_DISPOSITION_TYPES).map(
  type => ({ key: type, value: type, text: type })
);

const CallDispositionFormBody = ({ parentOptions }) => {
  const { setValue } = useFormContext();
  const parentId = useWatch({ name: "parent_id" });
  const type = useWatch({ name: "type" });

  const handleParentChange = value => {
    const parent = parentOptions.find(opt => opt.value === value);
    setValue("type", parent?.type ?? "", { shouldValidate: true });
  };

  return (
    <>
      <FormInput name="name" label="Name" required />
      {parentOptions.length > 0 && (
        <FormSelect
          name="parent_id"
          label="Parent Disposition"
          options={parentOptions}
          clearable
          onValueChange={handleParentChange}
        />
      )}
      <FormSelect
        name="type"
        label="Type"
        required
        options={dispositionTypeOptions}
        disabled={!!parentId}
      />
      <FormToggle name="dnc" label="DNC" />
      <FormToggle name="invalid" label="Invalid" />
      {type === CALL_DISPOSITION_TYPES.CONNECTED && (
        <FormToggle name="is_successful" label="Is Successful?" />
      )}
    </>
  );
};

CallDispositionFormBody.propTypes = {
  parentOptions: PropTypes.array.isRequired,
};

const CallDispositionForm = ({
  model,
  onSuccess,
  onCancel,
  parentDispositions = [],
}) => {
  const isEdit = !!model?.id;
  const { data: fetched, isLoading } = useQuery({
    queryKey: ["disposition", model?.id],
    queryFn: () => CallDispositionService.getDisposition(model.id),
    enabled: isEdit,
  });
  const { create, update } = useCallDispositionsMutations();

  const source = fetched ?? (isEdit ? null : model);

  const parentOptions = useMemo(
    () =>
      parentDispositions
        .filter(p => p.id !== source?.id)
        .map(p => ({
          key: p.id,
          value: p.id,
          text: p.name,
          type: p.type,
        })),
    [parentDispositions, source?.id]
  );

  if (isEdit && (isLoading || !source)) {
    return (
      <div className="tw:flex tw:items-center tw:justify-center tw:p-6">
        <Spinner />
      </div>
    );
  }

  const defaults = {
    name: source?.name ?? "",
    type: source?.type ?? "",
    parent_id: source?.parent_id ?? null,
    dnc: source?.dnc ?? false,
    invalid: source?.invalid ?? false,
    is_successful: source?.is_successful ?? false,
  };

  const handleSubmit = async values => {
    const data = {
      name: values.name,
      type: values.type,
      dnc: values.dnc,
      invalid: values.invalid,
      parent_id: values.parent_id || null,
      is_successful:
        values.type === CALL_DISPOSITION_TYPES.CONNECTED
          ? values.is_successful
          : false,
      assign_to_self: false,
    };
    if (isEdit) {
      await update.mutateAsync({ id: source.id, data });
    } else {
      await create.mutateAsync(data);
    }
  };

  const handleSuccess = () => {
    toast.success(isEdit ? "Disposition updated" : "Disposition created");
    onSuccess?.();
  };

  return (
    <BaseForm
      schema={schema}
      defaultValues={defaults}
      onSubmit={handleSubmit}
      onSuccess={handleSuccess}
      onCancel={onCancel}
      submitButtonText="Save"
    >
      <CallDispositionFormBody parentOptions={parentOptions} />
    </BaseForm>
  );
};

CallDispositionForm.propTypes = {
  model: PropTypes.object,
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  parentDispositions: PropTypes.array,
};

export default CallDispositionForm;
